/**
 * ChatAssistant - AI-powered operations assistant (chatbot)
 *
 * Modular component that can be placed in any page.
 * Floating or embedded mode with conversation history.
 * Poupatempo-style interface for operational queries.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Card,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  CircularProgress,
  Fab,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Tooltip,
  Badge,
  Chip
} from '@mui/material';
import {
  Send,
  SmartToy,
  Person,
  Chat as ChatIcon,
  Close,
  History,
  Add
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import insightsService from '../../services/insightsService';
import ChatMarkdown from '../common/ChatMarkdown';
import ptChatMessages from '../../i18n/locales/pt/chat.json';
import enChatMessages from '../../i18n/locales/en/chat.json';
import esChatMessages from '../../i18n/locales/es/chat.json';

/**
 * Wrapper around the shared ``ChatMarkdown`` renderer.
 *
 * The previous artisanal regex-based formatter only handled
 * ``**bold**``, ``*italic*``, `` `code` ``, bullets and numbered lists.
 * Anything else the LLM emitted (notably ``#``/``##``/``###`` headings,
 * ``>`` quotes, links, ``---`` rules, and tables) leaked through as raw
 * ``#`` characters and looked broken to users (#2404). We now delegate
 * to ``react-markdown`` + ``remark-gfm`` via ``ChatMarkdown`` so the
 * assistant gets the same bubble-friendly typography as the public
 * agent chat.
 */
const FormattedMessage = ({ content }) => <ChatMarkdown content={content} />;

const chatFallbacks = {
  pt: ptChatMessages.chat,
  en: enChatMessages.chat,
  es: esChatMessages.chat
};

const resolveBaseLanguage = (language) => String(language || 'pt').split('-')[0].toLowerCase();

const getNestedValue = (source, key) => (
  key
    .split('.')
    .reduce((current, part) => (current && current[part] !== undefined ? current[part] : undefined), source)
);

const interpolateFallback = (template, options = {}) => (
  Object.entries(options).reduce(
    (message, [token, value]) => message.replaceAll(`{{${token}}}`, value ?? ''),
    template
  )
);

const ChatAssistant = ({
  floating = false,
  contextType = 'general',
  height = 500,
  onClose,
  initialContext = null,
  onContextCleared
}) => {
  const { t, i18n, ready } = useTranslation();
  const [open, setOpen] = useState(!floating);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const locale = i18n.resolvedLanguage || i18n.language || undefined;
  const translateLabel = useCallback((key, options = {}) => {
    if (ready && i18n.isInitialized && i18n.exists(key)) {
      return t(key, options);
    }

    const baseLanguage = resolveBaseLanguage(i18n.resolvedLanguage || i18n.language);
    const fallbackValue =
      getNestedValue(chatFallbacks[baseLanguage], key.replace(/^chat\./, '')) ??
      getNestedValue(chatFallbacks.en, key.replace(/^chat\./, ''));

    if (typeof fallbackValue === 'string') {
      return interpolateFallback(fallbackValue, options);
    }

    return key;
  }, [i18n, ready, t]);

  const quickActions = [
    {
      label: translateLabel('chat.quickActions.recentFailures.label'),
      query: translateLabel('chat.quickActions.recentFailures.query')
    },
    {
      label: translateLabel('chat.quickActions.executors.label'),
      query: translateLabel('chat.quickActions.executors.query')
    },
    {
      label: translateLabel('chat.quickActions.schedules.label'),
      query: translateLabel('chat.quickActions.schedules.query')
    }
  ];

  const normalizeText = (value) => (
    value
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
  );

  const formatSessionDate = (value) => (
    new Intl.DateTimeFormat(locale).format(new Date(value))
  );

  useEffect(() => {
    if (floating && open) {
      document.body.classList.add('insight-chat-open');
    } else if (floating) {
      document.body.classList.remove('insight-chat-open');
    }

    return () => {
      if (floating) {
        document.body.classList.remove('insight-chat-open');
      }
    };
  }, [floating, open]);

  useEffect(() => {
    if (!initialContext) {
      return undefined;
    }

    setOpen(true);

    const contextMessage = translateLabel('chat.contextMessage', {
      title: initialContext.title,
      description: initialContext.description
    });

    setMessages([
      {
        role: 'assistant',
        content: translateLabel('chat.welcome')
      },
      {
        role: 'user',
        content: contextMessage
      }
    ]);
    setSessionId(null);

    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await insightsService.sendMessage(contextMessage, null, 'alert');
        setSessionId(response.session_id);
        setMessages((prev) => [...prev, { role: 'assistant', content: response.message }]);
      } catch (error) {
        setMessages((prev) => [...prev, {
          role: 'assistant',
          content: translateLabel('chat.mockResponses.general')
        }]);
      } finally {
        setLoading(false);
      }
    }, 100);

    if (onContextCleared) {
      onContextCleared();
    }

    return () => clearTimeout(timer);
  }, [initialContext, onContextCleared, translateLabel]);

  useEffect(() => {
    if (!open || initialContext) {
      return;
    }

    loadSessions();

    if (messages.length === 0) {
      setMessages([
        {
          role: 'assistant',
          content: translateLabel('chat.welcomeDetailed')
        }
      ]);
    }

    const timer = setTimeout(() => inputRef.current?.focus(), 100);
    return () => clearTimeout(timer);
  }, [initialContext, messages.length, open, translateLabel]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView?.({ behavior: 'smooth' });
  }, [messages]);

  const loadSessions = async () => {
    try {
      const data = await insightsService.getChatSessions(10);
      setSessions(data.sessions || []);
    } catch (error) {
      console.error('Failed to load sessions:', error);
    }
  };

  const loadSession = async (session) => {
    try {
      const data = await insightsService.getSessionMessages(session.session_id);
      setMessages(data.messages || []);
      setSessionId(session.session_id);
      setShowHistory(false);
    } catch (error) {
      console.error('Failed to load session:', error);
    }
  };

  const startNewSession = () => {
    setMessages([
      {
        role: 'assistant',
        content: translateLabel('chat.welcome')
      }
    ]);
    setSessionId(null);
    setShowHistory(false);
  };

  const getMockResponse = (question) => {
    const normalizedQuestion = normalizeText(question);

    const hasFailureIntent = [
      'falha', 'falhas', 'erro', 'erros', 'problema', 'problemas',
      'failure', 'failures', 'problem', 'problems', 'falla', 'fallas'
    ].some((term) => normalizedQuestion.includes(term));

    if (hasFailureIntent) {
      return translateLabel('chat.mockResponses.failures');
    }

    const hasExecutorIntent = [
      'executor', 'executors', 'agente', 'agentes',
      'agent', 'agents', 'ejecutor', 'ejecutores'
    ].some((term) => normalizedQuestion.includes(term));

    if (hasExecutorIntent) {
      return translateLabel('chat.mockResponses.executors');
    }

    const hasScheduleIntent = [
      'agendamento', 'agendamentos', 'schedule', 'schedules',
      'programacion', 'programaciones', 'cronograma', 'cronogramas'
    ].some((term) => normalizedQuestion.includes(term));

    if (hasScheduleIntent) {
      return translateLabel('chat.mockResponses.schedules');
    }

    return translateLabel('chat.mockResponses.general');
  };

  const handleSend = async () => {
    if (!input.trim() || loading) {
      return;
    }

    const userMessage = input.trim();
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: userMessage }]);
    setLoading(true);

    try {
      const response = await insightsService.sendMessage(userMessage, sessionId, contextType);
      setSessionId(response.session_id);
      setMessages((prev) => [...prev, { role: 'assistant', content: response.message }]);
    } catch (error) {
      setMessages((prev) => [...prev, {
        role: 'assistant',
        content: getMockResponse(userMessage)
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (onClose) {
      onClose();
    }
  };

  const chatContent = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'primary.main',
          color: 'primary.contrastText'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SmartToy />
          <Typography variant="h6">{translateLabel('chat.title')}</Typography>
        </Box>
        <Box>
          <Tooltip title={translateLabel('chat.newChat')}>
            <IconButton
              size="small"
              onClick={startNewSession}
              sx={{ color: 'inherit' }}
              aria-label={translateLabel('chat.newChat')}
            >
              <Add />
            </IconButton>
          </Tooltip>
          <Tooltip title={translateLabel('chat.history')}>
            <IconButton
              size="small"
              onClick={() => setShowHistory(!showHistory)}
              sx={{ color: 'inherit' }}
              aria-label={translateLabel('chat.history')}
            >
              <Badge badgeContent={sessions.length} color="secondary" max={9}>
                <History />
              </Badge>
            </IconButton>
          </Tooltip>
          {floating && (
            <Tooltip title={t('common.close')}>
              <IconButton
                size="small"
                onClick={handleClose}
                sx={{ color: 'inherit' }}
                aria-label={t('common.close')}
              >
                <Close />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', p: 2, bgcolor: 'grey.50' }}>
        {messages.map((msg, idx) => (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              mb: 2
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 1,
                maxWidth: '85%',
                flexDirection: msg.role === 'user' ? 'row-reverse' : 'row'
              }}
            >
              <Avatar
                sx={{
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'secondary.main',
                  width: 32,
                  height: 32
                }}
              >
                {msg.role === 'user' ? <Person fontSize="small" /> : <SmartToy fontSize="small" />}
              </Avatar>
              <Paper
                elevation={1}
                sx={{
                  p: 1.5,
                  bgcolor: msg.role === 'user' ? 'primary.main' : 'background.paper',
                  color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 2,
                  borderTopLeftRadius: msg.role === 'user' ? 16 : 4,
                  borderTopRightRadius: msg.role === 'user' ? 4 : 16
                }}
              >
                {msg.role === 'assistant' ? (
                  <FormattedMessage content={msg.content} />
                ) : (
                  <Typography variant="body2">{msg.content}</Typography>
                )}
              </Paper>
            </Box>
          </Box>
        ))}

        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 5 }}>
            <CircularProgress size={20} />
            <Typography variant="body2" color="text.secondary">
              {translateLabel('chat.thinking')}
            </Typography>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      <Box sx={{ px: 2, py: 1, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {quickActions.map((action, idx) => (
            <Chip
              key={idx}
              label={action.label}
              size="small"
              variant="outlined"
              onClick={() => {
                setInput(action.query);
                inputRef.current?.focus();
              }}
              sx={{
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'primary.light',
                  color: 'primary.contrastText'
                }
              }}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <TextField
            inputRef={inputRef}
            fullWidth
            size="small"
            placeholder={translateLabel('chat.placeholder')}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            multiline
            maxRows={3}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 3
              }
            }}
          />
          <IconButton
            color="primary"
            onClick={handleSend}
            disabled={!input.trim() || loading}
            aria-label={translateLabel('chat.send')}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': { bgcolor: 'primary.dark' },
              '&:disabled': { bgcolor: 'grey.300' }
            }}
          >
            <Send />
          </IconButton>
        </Box>
      </Box>

      <Drawer anchor="right" open={showHistory} onClose={() => setShowHistory(false)}>
        <Box sx={{ width: 280 }}>
          <Box sx={{ p: 2, bgcolor: 'primary.main', color: 'primary.contrastText' }}>
            <Typography variant="h6">{translateLabel('chat.historyTitle')}</Typography>
          </Box>
          <List>
            {sessions.length === 0 ? (
              <ListItem>
                <ListItemText
                  primary={translateLabel('chat.noHistory')}
                  primaryTypographyProps={{ color: 'text.secondary' }}
                />
              </ListItem>
            ) : (
              sessions.map((session) => (
                <ListItemButton
                  key={session.session_id}
                  onClick={() => loadSession(session)}
                  sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                  <ListItemText
                    primary={session.title || translateLabel('chat.untitled')}
                    secondary={formatSessionDate(session.updated_at)}
                  />
                </ListItemButton>
              ))
            )}
          </List>
        </Box>
      </Drawer>
    </Box>
  );

  if (floating) {
    return (
      <>
        <Tooltip title={translateLabel('chat.open')}>
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 32,
              right: 32,
              zIndex: 9999,
              width: 56,
              height: 56,
              boxShadow: '0 4px 20px rgba(1, 111, 244, 0.4)',
              background: 'linear-gradient(180deg, #38BCFF 0%, #20A4FE 25%, #1991FA 50%, #0B82F9 75%, #016FF4 100%)',
              '&:hover': {
                background: 'linear-gradient(180deg, #20A4FE 0%, #1991FA 25%, #0B82F9 50%, #016FF4 75%, #0158C7 100%)',
                boxShadow: '0 6px 24px rgba(1, 111, 244, 0.5)',
                transform: 'scale(1.05)'
              },
              transition: 'all 0.2s ease-in-out'
            }}
            onClick={handleOpen}
            aria-label={translateLabel('chat.open')}
          >
            <ChatIcon />
          </Fab>
        </Tooltip>
        <Drawer
          anchor="right"
          open={open}
          onClose={handleClose}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 420 },
              maxWidth: '100vw'
            }
          }}
        >
          {chatContent}
        </Drawer>
      </>
    );
  }

  return <Card sx={{ height }}>{chatContent}</Card>;
};

export default ChatAssistant;
